import { StoreData, CompositeKeys, Entity, StoreField, StoreItemPath } from 'app/types-shared';
import { deepEquals } from 'app/infrastructure/js';
import { getPrimaryEntityId } from 'app/infrastructure/store/entity';

/**
 * Returns an array of CompositeKeys for all entities in provided StoreData
 *
 * @param data array of store data
 */
export const getIds = (data: StoreData) =>
  data.reduce(
    (res, value) => ({
      ...res,
      [JSON.stringify(value.id)]: value.id
    }),
    {} as { [x: string]: CompositeKeys }
  );

/**
 * Finds Entity for given CompositeKey
 *
 * @param {StoreData} data store state
 * @param {CompositeKeys} path path to item to be found
 * @returns {StoreItem} store item at the given path
 */
export const getById = (data: StoreData, keys: CompositeKeys): Entity => {
  return data
    ? Array.isArray(data) && data.find(item => deepEquals(item.id, keys))
    : undefined;
};

/**
 * Resolves the caseContextId and returns it as a typed `number`
 *
 * @param storeData data store state
 */
export const getCaseContextId = (storeData: StoreData): number => {
  const entity = Array.isArray(storeData) ? storeData[0] : storeData;
  const caseContextField = entity && entity.caseContext;
  const caseContextId = 
    caseContextField && caseContextField[0] && (caseContextField[0] as StoreField).value;
  return caseContextField && caseContextId ? Number(caseContextId) : undefined;
};

/**
 * Resolves the CaseNo property and returns it as a typed `number`
 *
 * @param storeData The case Entity, or the Entity array for a complete case
 */
export const getCaseId = (storeData: StoreData): number => {
  const entity = Array.isArray(storeData) ? storeData[0] : storeData;
  const caseNoField = entity && entity.CaseNo;
  const no =
    caseNoField && caseNoField[0] && (caseNoField[0] as StoreField).value;
  return caseNoField && no ? Number(no) : undefined;
};

/**
 * Resolves the Primary Key (Trans) from the root table (tblTrans)
 *
 * @param storeData The case Entity, or the Entity array for a complete case
 */
export const getDraftId = (storeData: StoreData) => {
  const entity = Array.isArray(storeData) ? storeData[0] : storeData;
  const id =
    entity &&
    Array.isArray(entity.id) &&
    entity.id.length &&
    Number(entity.id[0].tblTrans);
  return entity && id ? id : undefined;
};
/**
 * Return the parent id of the child identified by a path
 *
 * @param childPath
 */
export const getParentId = (childPath: CompositeKeys) => {
  return childPath && childPath.length > 1
    ? childPath.slice(0, childPath.length - 1)
    : undefined;
};
/**
 * Get first level of child entities in store that has a given parent
 *
 * @param data
 * @param parentStorePath
 */
export const getAllChildEntities = (
  data: StoreData,
  parentStorePath: CompositeKeys
): Entity[] => {
  return data.filter(
    e =>
      e.id?.length > 1 &&
      !deepEquals(parentStorePath, e.id) && //Exclude parent.
      deepEquals(parentStorePath, e.id?.slice(0, e.id?.length - 1)) //Compare with part of path shared with parent.
  );
};

/**
 * Select all child entities at given store path
 *
 * @param {StoreData} data store state
 * @param {CompositeKeys} parentKeys path to the parent
 * @param {string} childEntityType the type of the entity to be found
 * @returns {StoreItem} store item at the given path
 */
export const getChildEntities = (
  data: StoreData,
  parentStorePath: CompositeKeys,
  childEntityType: string
) => {
  return data && parentStorePath
    ? data.filter(item => {
        if (
          !item ||
          !item.id ||
          item.id.length !== parentStorePath.length + 1
        ) {
          return false;
        }
        const key = item.id.slice(0, parentStorePath.length);
        const lastItemKey = Object.keys(item.id[item.id.length - 1]);
        const childType = lastItemKey.length === 1 && lastItemKey[0];
        return (
          childType === childEntityType && deepEquals(key, parentStorePath)
        );
      })
    : [];
};

/**
 * Select all child entities at a given path, regardless of ids at each level.
 *
 * @param {StoreData} data store state
 * @returns {string[]} entity type path
 */
export const getAllEntitiesAtPath = (
  data: StoreData,
  entityTypePath: (string | StoreItemPath)[]
) => {
  return data && entityTypePath
    ? data.filter(item => {
        if (
          !item ||
          !item.id ||
          item.id.length !== entityTypePath.length
        ) {
          return false;
        }
        return item.id.every(
          (i, index) =>
            (typeof entityTypePath[index] === 'string') ?
              Object.keys(i)[0] === entityTypePath[index] :
              deepEquals(i, entityTypePath[index])
        );
      })
    : [];
};

/**
 * Gets the max value from the child reference array
 *
 * @param parent The parent entity.
 * @param childEntityType the name of the entity type to find ID for
 */
export const maxChildEntityId = (
  data: StoreData,
  parentStorePath: CompositeKeys,
  childEntityType: string
) => {
  const children = getChildEntities(data, parentStorePath, childEntityType).map(
    getPrimaryEntityId
  );

  if (!children || !children.length) {
    return 0;
  }

  return Math.max(...children);
};
